import { isDestroyed, isDestroying } from '@ember/destroyable';
import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { timeout } from 'lumen/utils/waiters';

interface HotelAndMarketFormFieldsHotelOrMarketSearchSignature {
  Args: {
    hotelOnly?: boolean;
    selectItem: unknown;
    selectedOption: unknown;
  };
}

export default class HotelAndMarketFormFieldsHotelOrMarketSearch extends Component<HotelAndMarketFormFieldsHotelOrMarketSearchSignature> {
  @service declare ajax: Services['ajax'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  @tracked isOpen = false;
  @tracked options = [];
  @tracked optionsFilter = '';
  @tracked page = 0;
  @tracked totalPages = 1;

  fetchOptionsTask = task({ restartable: true }, async () => {
    const data = {
      direct: true,
      page: this.page,
      q: this.optionsFilter,
    };

    // @ts-expect-error: Incorrect type
    let options = [];

    // When querying hotels and markets, request to return all My Hotels submarkets
    if (!this.hotelOnly) {
      // @ts-expect-error: Incorrect type
      data.force_myhotels_breakdown = true;
    }

    try {
      const url = this.hotelOnly
        ? '/autocomplete/hotels/'
        : '/autocomplete/hotelsmarkets/';

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { meta, results } = await this.ajax.request(url, {
        data,
        method: 'GET',
      });

      // If we are searching for a hotel or a market, the default option (all) should not be added
      if (!this.optionsFilter) {
        options.push(this.defaultOption);
      }

      const newOptions = this.hotelAndMarketService
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        .formatAutocompleteResults(results)
        .map((hotelOrMarket) => {
          const {
            hotel_id,
            market_id,
            name,
            products,
            subscription_type,
            // @ts-expect-error: Incorrect type
            type,
          } = hotelOrMarket;

          return {
            badge: subscription_type,
            hotel_id,
            label: name,
            market_id,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            type,
            unsafe_subLabel: products.join(', '),
          };
        });

      // Add the results of the query
      options = [...options, ...newOptions];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.totalPages = Math.ceil(meta.results_count / meta.results_per_page);
    } catch (error) {
      this.errorTracking.addBreadcrumb({
        data: {
          error,
        },
      });
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return options;
  });

  loadMoreTask = task({ restartable: true }, async () => {
    if (this.page + 1 < this.totalPages) {
      this.page += 1;
      const fetchedOptions = await this.fetchOptionsTask.perform();
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment
      this.options = [...this.options, ...fetchedOptions];
    }
  });

  searchOptionTask = task({ restartable: true }, async (optionsFilter) => {
    await timeout();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.optionsFilter = optionsFilter;
    this.page = 0;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.options = await this.fetchOptionsTask.perform();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultOption() {
    const label = this.hotelOnly
      ? this.intl.t('reports.all_hotels_filters')
      : this.intl.t('reports.all_hotels_markets_filters');

    return {
      label,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelOnly() {
    return this.args.hotelOnly ?? false;
  }

  resetValues(): void {
    this.optionsFilter = '';
    this.page = 0;
    this.options = [];
  }

  @action
  // @ts-expect-error: Incorrect type
  selectOption(option): void {
    this.optionsFilter = '';
    // @ts-expect-error: Incorrect type
    this.args.selectItem(option);
  }

  @action
  toggleDropdown(): void {
    if (isDestroyed(this) || isDestroying(this)) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      void this.searchOptionTask.perform(this.optionsFilter);
    } else {
      this.resetValues();
    }
  }

  <template>
    <PrismComboSelectInput
      @horizontalPosition="right"
      @isSearchable={{true}}
      @loadingMessage={{concat (t "general.loading") "..."}}
      @onChange={{this.selectOption}}
      @onClose={{this.toggleDropdown}}
      @onOpen={{this.toggleDropdown}}
      @onSearch={{this.searchOptionTask.perform}}
      @options={{this.options}}
      @unsafe_dropdownClass="hotel-selection-dropdown-old__content"
      @unsafe_isClampedToOneLine={{true}}
      @unsafe_onLoadMore={{this.loadMoreTask.perform}}
      @value={{if @selectedOption @selectedOption this.defaultOption}}
      data-test-id="hotel-or-market-search-dropdown"
    />
  </template>
}
